import { useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { IEmpresas } from '../../interfaces/IEmpresas';
import { IResumo } from '../../interfaces/IRelatorio';
import RelatoriosService from '../../services/RelatoriosService';
import { CircularProgressbar, CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import RadialSeparators from "./RadialSeparators";
import { Tipo } from './Relatorio';

type CampoGrande = {
    cor: string;
    valor: number;
    texto: string;
}
type CamposPequenos = {
    porcentagem: number;
    corPrimaria: string;
    corSecundaria: string;
    valor: number;
    texto: string;
    porcentagemTexto: string;
    tipo: Tipo;
}

type EcdByYear = {
    ano: string,
    valor: number
}

function RelatorioResumo({ empresa, dataInicial, dataFinal }: { empresa: IEmpresas | undefined, dataInicial: Date | undefined, dataFinal: Date | undefined }) {

    const history = useHistory();
    const [carregado, setCarregado] = useState<boolean>(false);
    const camposTaxas = [
        { texto: 'Juros e multa', valor: 0, cor: '#5E6397'  },
        { texto: 'Multa sem declarações', valor: 0, cor: '#8E92B8'  }
    ];
    const [camposGrandes, setCamposGrandes] = useState<CampoGrande[]>([
        { cor: '#5E6397', valor: 5, texto: 'Contas analisadas' },
        { cor: '#8E92B8', valor: 2068, texto: 'Lançamentos analisados' }
    ]);
    const [grafico, setGrafico] = useState<EcdByYear[]>([]);
    const [graficoTotal, setGraficoTotal] = useState<EcdByYear[]>([]);
    const [camposPequenos, setCamposPequenos] = useState<CamposPequenos[]>([
        { corPrimaria: '#EB1F44', corSecundaria: '#E6C8CD', valor: 139, texto: 'Erros encontrados', porcentagemTexto: '7,7', porcentagem: 17, tipo: Tipo.Erro },
        { corPrimaria: '#FFB629', corSecundaria: '#FEF4D9', valor: 156, texto: 'Avisos', porcentagemTexto: '8', porcentagem: 10, tipo: Tipo.Aviso },
        { corPrimaria: '#00B757', corSecundaria: '#E0F7F1', valor: 295, texto: 'Recomendações', porcentagemTexto: '10,2', porcentagem: 10, tipo: Tipo.Recomendacao },
        { corPrimaria: '#00B7A1', corSecundaria: '#E0F7F1', valor: 6, texto: 'Pendentes de conciliação', porcentagemTexto: '0,3', porcentagem: 10, tipo: Tipo.Pendente },
        { corPrimaria: '#00B7A1', corSecundaria: '#E0F7F1', valor: 2, texto: 'Matches de conciliação', porcentagemTexto: '33', porcentagem: 33, tipo: Tipo.Match }
    ]);

    useEffect(() => {
        async function preCarregar() {
            setCarregado(false);

            if (empresa?.id && dataInicial && dataFinal) {
                await RelatoriosService.dadosDashboard(empresa.id, dataInicial, dataFinal, funcaoSucesso);
                setCarregado(true);
            }
        }

        setTimeout(() => {
            preCarregar();
        }, 1000)

        // eslint-disable-next-line
    }, [empresa, dataInicial, dataFinal])

    function funcaoSucesso(data:  IResumo) {
        if (data) {
            if (data.ecdByYear != null) {
                const aux = Object.keys(data.ecdByYear).map((item) => {
                    return {
                        ano: item,
                        valor: data.ecdByYear[parseInt(item)]
                    }
                });

                setGrafico(aux);
            }

            if (data.sumPendenteRecolhimentoByYear != null) {
                const aux = Object.keys(data.sumPendenteRecolhimentoByYear).map((item) => {
                    return {
                        ano: item,
                        valor: data.sumPendenteRecolhimentoByYear[parseInt(item)]
                    }
                });

                setGraficoTotal(aux);
            }

            const porcentagemErro = pegarPorcentagem(data.erros, data.lancamentosAnalisados);
            const porcentagemAvisos = pegarPorcentagem(data.avisos, data.lancamentosAnalisados);
            const porcentagemRecomendacoes = pegarPorcentagem(data.recomendacoes, data.lancamentosAnalisados);
            const porcentagemPendentes = pegarPorcentagem(data.pendentesCOnciliacao, data.lancamentosAnalisados);
            const porcentagemMatches = pegarPorcentagem(data.matchesConciliacao, data.lancamentosAnalisados);
            let camposGrandesTemp = camposGrandes;
            let camposPequenosTemp = camposPequenos;

            camposGrandesTemp[0].valor = data.contasAnalisadas;
            camposGrandesTemp[1].valor = data.lancamentosAnalisados;
            camposPequenosTemp[0].valor = data.erros;
            camposPequenosTemp[0].porcentagem = porcentagemErro['valor'];
            camposPequenosTemp[0].porcentagemTexto = porcentagemErro['texto'];
            camposPequenosTemp[1].valor = data.avisos;
            camposPequenosTemp[1].porcentagem = porcentagemAvisos['valor'];
            camposPequenosTemp[1].porcentagemTexto = porcentagemAvisos['texto'];
            camposPequenosTemp[2].valor = data.recomendacoes;
            camposPequenosTemp[2].porcentagem = porcentagemRecomendacoes['valor'];
            camposPequenosTemp[2].porcentagemTexto = porcentagemRecomendacoes['texto'];
            camposPequenosTemp[3].valor = data.pendentesCOnciliacao;
            camposPequenosTemp[3].porcentagem = porcentagemPendentes['valor'];
            camposPequenosTemp[3].porcentagemTexto = porcentagemPendentes['texto'];
            camposPequenosTemp[4].valor = data.matchesConciliacao;
            camposPequenosTemp[4].porcentagem = porcentagemMatches['valor'];
            camposPequenosTemp[4].porcentagemTexto = porcentagemMatches['texto'];

            setCamposGrandes(camposGrandesTemp)
            setCamposPequenos(camposPequenosTemp)
        }
    };

    function pegarPorcentagem(valorAtual: number, valorTotal: number): { valor: number, texto: string } {
        const porcentagem = valorAtual !== 0 && valorTotal !== 0 ? valorAtual * 100 / valorTotal : 0;
        const valor = porcentagem <= 1 && porcentagem !== 0 ? 1 : porcentagem;
        const texto = porcentagem > 0.00 ? porcentagem.toFixed(2).replace(".", ",") : "0";

        return { valor, texto };
    }

    return <>
    {
        dataInicial && dataFinal ?
            carregado ?
                <div style={{ userSelect: 'none',  border: '1px solid #ddd', padding: '10px', width: '100%' }}>
                    <div className="row" style={{ marginBottom: '25px' }}>
                        <div className='col-md-2'>
                            <div style={{ margin: '10px 10px 0 0', border: '1px solid #ddd', borderRadius: '5px', padding: '10px', height: '100%', textAlign: 'center' }}>
                                <label style={{ fontSize: '16px' }}>ECD por ano</label>
                                <div style={{ display: 'flex', marginTop: '20px' }}>
                                    {
                                        grafico.sort((a, b) => b.valor - a.valor).map((x, index) => {
                                            const altura = 100 * x.valor / Math.max(...grafico.map(x => x.valor));

                                            return <div key={index} style={{ height: '135px', width: `${100 / grafico.length}%`, marginRight: '3%', justifyContent: 'center' }}>
                                                <label style={{ fontSize: '12px', color: 'black', width: '100%' }}>{x.valor}</label>
                                                <div style={{ display: 'grid', height: '70%', justifyContent: 'center', alignItems: 'end' }}>
                                                    <div style={{ gridColumnStart: '1', gridRowStart: '1', width: '20px', height: '100%', backgroundColor: '#E0F7F1' }} />
                                                    <div style={{ gridColumnStart: '1', gridRowStart: '1', width: '20px', height: `${altura}%`, backgroundColor: '#00B7A1' }} />
                                                </div>
                                                <label style={{ fontSize: '12px', color: 'black' }}>{x.ano}</label>
                                            </div>
                                        })
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div style={{ margin: '10px 10px 0 0', border: '1px solid #ddd', borderRadius: '5px', padding: '10px', height: '100%', textAlign: 'center' }}>
                                <label style={{ fontSize: '16px' }}>Total tributos em aberto por ano</label>
                                <div style={{ display: 'flex', marginTop: '20px' }}>
                                    {
                                        graficoTotal.sort((a, b) => b.valor - a.valor).map((x, index) => {
                                            const altura = 100 * x.valor / Math.max(...graficoTotal.map(x => x.valor));

                                            return <div key={index} style={{ height: '135px', width: `${100 / graficoTotal.length}%`, marginRight: '3%', justifyContent: 'center' }}>
                                                <label style={{ fontSize: '12px', color: 'black', width: '100%' }}>{x.valor.toLocaleString('pt-br',{style: 'currency', currency: 'BRL'})}</label>
                                                <div style={{ display: 'grid', height: '70%', justifyContent: 'center', alignItems: 'end' }}>
                                                    <div style={{ gridColumnStart: '1', gridRowStart: '1', width: '20px', height: '100%', backgroundColor: '#E0F7F1' }} />
                                                    <div style={{ gridColumnStart: '1', gridRowStart: '1', width: '20px', height: `${altura}%`, backgroundColor: '#00B7A1' }} />
                                                </div>
                                                <label style={{ fontSize: '12px', color: 'black' }}>{x.ano}</label>
                                            </div>
                                        })
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3" >
                            <div style={{display: 'flex',  height: '100%',  margin: '10px 10px 0 0'}}>
                                {
                                    camposTaxas.map((x, index) =>
                                        <div key={index} style={{ display: 'grid', border: '1px solid #ddd', borderRadius: '5px', height: '100%', width: `${100 / camposTaxas.length}%`, marginRight: '3%', textAlign: 'center' }}>
                                            <div style={{ marginTop: '10%', marginLeft: 'auto', marginRight: 'auto', width: '70%' }}>
                                                <CircularProgressbar value={100} text={`${x.valor}`} styles={buildStyles({ textColor: x.cor, pathColor: x.cor })} />
                                            </div>
                                            <label style={{ fontSize: '0.875em', color: 'black' }}>{x.texto}</label>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-9">
                            <div style={{display: 'flex', width:'100%'}}>
                                {
                                    camposGrandes.map((x, index) => 
                                        <div key={index} style={{ display: 'flex', border: '1px solid #ddd', borderRadius: '5px', height: '200px', width: '100%', marginRight: index !== camposGrandes.length - 1 ? '1%' : '0', alignItems: 'center' }}>
                                            <div style={{ width: '35%', position: 'relative', left: '5%' }}>
                                                <CircularProgressbar value={100} text={`${x.valor}`} styles={buildStyles({ textColor: x.cor, pathColor: x.cor })} />
                                            </div>
                                            <label style={{ fontSize: '1.250em', color: 'black', height: 'fitContent', margin: '0 auto 0 8%' }}>{x.texto}</label>
                                        </div>
                                    )
                                }
                            </div>
                            <div style={{display: 'flex', marginTop: '10px'}}>
                                {
                                    camposPequenos.map((x, index) => 
                                        <div key={index} onClick={() => { history.push(`${empresa?.id}/${x.tipo}`); localStorage.setItem(`${x.tipo}`, localStorage.getItem(`${Tipo.Relatorio}`) ?? "") } } style={{cursor: 'pointer', border: '1px solid #ddd', borderRadius: '5px', height: '180px', width: `${100 / camposPequenos.length}%`, marginRight: index !== camposPequenos.length - 1 ? '1%' : '0', textAlign: 'center', display: 'grid' }}>
                                            <div style={{ margin: '5% auto 10% auto', width: '65%' }}>
                                                <CircularProgressbarWithChildren value={x.porcentagem} text={`${x.valor}`} strokeWidth={10} styles={buildStyles({ textColor: x.corPrimaria, pathColor: x.corPrimaria, trailColor: x.corSecundaria, strokeLinecap: "butt" })}>
                                                    <RadialSeparators count={12} style={{ background: "#fff", width: "2px", height: `${10}%` }} />
                                                </CircularProgressbarWithChildren>
                                            </div>
                                            <div style={{ display: 'grid', gridAutoRows: 'minContent' }}>
                                                <label style={{ fontSize: '0.750em', color: 'black' }}>{x.porcentagemTexto} %</label>
                                                <label style={{ fontSize: '0.875em', color: 'black' }}>{x.texto}</label>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            : <p className="mt-5">Carregando...</p>
        : <h2 className="mt-5">Preencha o campo Período e clique em buscar para carregar os dados</h2>
    }
    </>;
};

export default RelatorioResumo;